import { DEBUG_EXPORT_PAGE_WRAPPER_ID, PAGE_WRAPPER_ID } from "common/constants";
import { getElementById } from "common/utils/dom";
import type { MouseEventHandler, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import cx from "ui/cx";
import { OPTIONS_BOUNDARY_PADDING, RIGHT_ALIGNED_OPTIONS_CLASS } from "ui/forms/ExpandableSelect";
import IconClose from "ui/icons/IconClose";
import IconGear from "ui/icons/IconGear";
import { Popover, type PopoverOpenChangeDetails } from "ui/popovers/Popover";
import styles from "./StudioFlyoutMenu.module.scss";

interface StudioFlyoutMenuContainerProps {
  onMouseEnter?: MouseEventHandler<HTMLDivElement>;
}

export const STUDIO_FLYOUT_MENU_ID = "studio-flyout-menu";

export interface StudioFlyoutMenuProps extends StudioFlyoutMenuContainerProps {
  children: ReactNode;
  title?: string;
  open: boolean;
  setOpen: (o: boolean) => void;
  // A custom element to anchor the flyout to
  anchorElement: ReactNode;
  actions?: ReactNode;
  onClose?: () => void;
  // if entity selection controls the opening of the flyout, we don't need the outside click handler
  useOutsideClickHandler?: boolean;
  resizable?: boolean;
  minWidth?: number;
  contentSide?: "left" | "right";
  contentAlign?: "start" | "center" | "end";
}

const VIEWPORT_PADDING = 12;
const RESIZABLE_MIN_WIDTH = 242;
const RESIZABLE_MAX_WIDTH = 560;

interface StudioFlyoutMenuResizeRef {
  startX: number;
  startWidth: number;
  currentWidth: number;
}

export default function StudioFlyoutMenu({
  children,
  title,
  actions = [],
  open,
  setOpen,
  anchorElement = null,
  onMouseEnter,
  onClose,
  useOutsideClickHandler = true,
  resizable = false,
  minWidth = RESIZABLE_MIN_WIDTH,
  contentSide = "left",
  contentAlign = "center",
}: StudioFlyoutMenuProps) {
  const pageEl = getElementById(PAGE_WRAPPER_ID) || getElementById(DEBUG_EXPORT_PAGE_WRAPPER_ID);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const flyoutHeaderRef = useRef<HTMLDivElement>(null);
  const flyoutBodyRef = useRef<HTMLDivElement>(null);
  const flyoutBodyContentRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const resizeStartRef = useRef<StudioFlyoutMenuResizeRef>({
    startX: 0,
    startWidth: 0,
    currentWidth: 0,
  });
  const [flyoutWidth, setFlyoutWidth] = useState<number | undefined>(undefined);
  const [isResizing, setIsResizing] = useState(false);
  const resolvedMinWidth = Math.max(0, minWidth);

  const constrainNestedOptionsWidth = useCallback((body: HTMLDivElement, constrainToBodyWidth: boolean) => {
    const options = body.getElementsByClassName("options") as HTMLCollectionOf<HTMLDivElement>;
    const bodyBox = body.getBoundingClientRect();

    for (const element of options) {
      if (!element?.style) {
        continue;
      }

      if (!constrainToBodyWidth) {
        element.style.maxWidth = "";
        continue;
      }

      const parentBox = element.parentElement?.getBoundingClientRect();
      if (!parentBox) {
        continue;
      }
      const className = element.parentElement?.className ?? "";
      const isRightAligned = className.includes(RIGHT_ALIGNED_OPTIONS_CLASS);
      const edgeDist = isRightAligned ? bodyBox.right - parentBox.right : parentBox.left - bodyBox.left;
      const width = bodyBox.width - edgeDist - OPTIONS_BOUNDARY_PADDING;
      element.style.maxWidth = `${width}px`;
    }
  }, []);

  const updateFlyoutLayout = useCallback(() => {
    const flyout = flyoutRef.current;
    const header = flyoutHeaderRef.current;
    const body = flyoutBodyRef.current;
    const bodyContent = flyoutBodyContentRef.current;

    if (!open || !flyout || !body || !bodyContent) {
      return;
    }

    body.style.overflowY = "visible";
    body.style.overflowX = "visible";
    body.style.maxHeight = "";

    const flyoutRect = flyout.getBoundingClientRect();
    const headerRect = header?.getBoundingClientRect();
    const headerHeight = headerRect?.height ?? 0;
    const availableBodyHeight = Math.floor(window.innerHeight - flyoutRect.top - headerHeight - VIEWPORT_PADDING);
    const shouldScrollVertically = availableBodyHeight > 0 && bodyContent.scrollHeight > availableBodyHeight;

    if (shouldScrollVertically) {
      body.style.overflowY = "auto";
      body.style.overflowX = "hidden";
      body.style.maxHeight = `${availableBodyHeight}px`;
    }

    constrainNestedOptionsWidth(body, shouldScrollVertically);
  }, [constrainNestedOptionsWidth, open]);

  const scheduleLayoutUpdate = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      updateFlyoutLayout();
      rafRef.current = null;
    });
  }, [updateFlyoutLayout]);

  useEffect(() => {
    if (!open) {
      return;
    }

    scheduleLayoutUpdate();
    window.addEventListener("resize", scheduleLayoutUpdate);

    const resizeObserver = new ResizeObserver(scheduleLayoutUpdate);
    const mutationObserver = new MutationObserver(scheduleLayoutUpdate);

    const flyout = flyoutRef.current;
    const header = flyoutHeaderRef.current;
    const body = flyoutBodyRef.current;
    const bodyContent = flyoutBodyContentRef.current;

    if (flyout) {
      resizeObserver.observe(flyout);
    }
    if (header) {
      resizeObserver.observe(header);
    }
    if (body) {
      resizeObserver.observe(body);
    }
    if (bodyContent) {
      resizeObserver.observe(bodyContent);
      mutationObserver.observe(bodyContent, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    return () => {
      window.removeEventListener("resize", scheduleLayoutUpdate);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [open, scheduleLayoutUpdate]);

  const handleResizeLeftEdgeStart = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const flyout = flyoutRef.current;
      if (!resizable || !flyout) {
        return;
      }

      const rect = flyout.getBoundingClientRect();
      resizeStartRef.current = {
        startX: event.clientX,
        startWidth: rect.width,
        currentWidth: rect.width,
      };
      setIsResizing(true);
    },
    [resizable],
  );

  useEffect(() => {
    if (!resizable || !open || !isResizing) {
      return;
    }

    const onMouseMove = (event: MouseEvent) => {
      event.preventDefault();

      const maxViewportWidth = Math.max(resolvedMinWidth, window.innerWidth - VIEWPORT_PADDING * 2);
      const unconstrainedWidth = resizeStartRef.current.startWidth - (event.clientX - resizeStartRef.current.startX);
      const maxAllowedWidth = Math.max(resolvedMinWidth, Math.min(RESIZABLE_MAX_WIDTH, maxViewportWidth));

      const width = Math.max(resolvedMinWidth, Math.min(unconstrainedWidth, maxAllowedWidth));
      resizeStartRef.current.currentWidth = width;

      if (flyoutRef.current) {
        flyoutRef.current.style.width = `${width}px`;
      }
    };

    const onMouseUp = () => {
      setFlyoutWidth(resizeStartRef.current.currentWidth);
      setIsResizing(false);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [isResizing, open, resizable, resolvedMinWidth]);

  const classNames = cx({ styles, wrapper: true });
  const childClassNames = cx({
    styles,
    flyoutButton: true,
    flyoutButtonActive: open,
  });

  // if a custom anchor is passed, do not apply default flyout styles to it
  const anchor = anchorElement ?? <IconGear size={19} className={open ? "icon-open" : undefined} />;
  const anchorClassNames = anchorElement ? "" : childClassNames;

  const commitActiveFlyoutInput = useCallback(() => {
    if (!open) {
      return;
    }

    const activeElement = document.activeElement;

    if (!(activeElement instanceof HTMLElement)) {
      return;
    }

    // if the active element is not a child of the flyout menu, abort
    if (!activeElement.closest(`#${STUDIO_FLYOUT_MENU_ID}`)) {
      return;
    }

    activeElement.blur();
  }, [open]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean, reason?: PopoverOpenChangeDetails["reason"]) => {
      // Mirrors the previous Radix onInteractOutside behavior: on outside-press/focus-out, keep the
      // flyout open when the consumer opts out of the outside-click handler, otherwise commit any
      // active input before closing.
      if (!nextOpen && (reason === "outside-press" || reason === "focus-out")) {
        if (!useOutsideClickHandler) {
          return;
        }
        commitActiveFlyoutInput();
      }
      if (!nextOpen) {
        onClose?.();
      }
      setOpen(nextOpen);
    },
    [onClose, setOpen, useOutsideClickHandler, commitActiveFlyoutInput],
  );

  // The close button only renders while the flyout is open, so no open-state guard is needed.
  const handleClose = useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);

  return (
    <Popover.Root open={open} onOpenChange={(nextOpen, details) => handleOpenChange(nextOpen, details?.reason)}>
      <div className={classNames}>
        {/* A real Base UI trigger (not a bare positioning anchor): Base UI owns the toggle and
            exempts the element from outside-press dismissal, so clicking it while open closes the
            flyout once instead of close-then-reopen. The Positioner anchors to it by default. */}
        <Popover.Trigger nativeButton={false}>
          <div className={anchorClassNames} data-testid="flyout-anchor-button">
            {anchor}
          </div>
        </Popover.Trigger>
        <Popover.Content
          side={contentSide}
          align={contentAlign}
          sideOffset={12.5}
          collisionPadding={12}
          container={pageEl ?? undefined}
          className={styles.popoverContent}
          // The flyout hosts focus-driven comboboxes (e.g. the Tag Template Select); pulling focus
          // into the popup on open steals it from those inputs and breaks their open/select flow.
          initialFocus={false}
        >
          <div
            ref={flyoutRef}
            className={cx({
              styles,
              flyoutMenu: true,
              isResizing,
            })}
            id={STUDIO_FLYOUT_MENU_ID}
            onMouseEnter={onMouseEnter}
            onClick={(event) => event.stopPropagation()}
            style={{
              minWidth: resolvedMinWidth,
              ...(flyoutWidth ? { width: flyoutWidth } : {}),
            }}
          >
            <div ref={flyoutHeaderRef} className={styles.flyoutMenuHeader}>
              <span>{title}</span>
              <div>
                {actions}
                <div
                  onClick={(event) => {
                    event.stopPropagation();
                    handleClose();
                  }}
                >
                  <IconClose size={13} />
                </div>
              </div>
            </div>
            <div ref={flyoutBodyRef} className={styles.flyoutMenuBody}>
              <div ref={flyoutBodyContentRef} className={styles.flyoutMenuBodyContent}>
                {children}
              </div>
            </div>
            {resizable && <div className={styles.resizeHandleLeftEdge} onMouseDown={handleResizeLeftEdgeStart} />}
          </div>
        </Popover.Content>
      </div>
    </Popover.Root>
  );
}
